/**
 * Filter card for the FP Monthly COGS Tracker.
 *
 * Reuses the shared filter fields (Search / FP Status / Market / Region)
 * so the tracker matches the Platform Reports filter vocabulary; pairs
 * them with a month-only Start / End range picker (rolling 12 months by
 * default). Lives outside ReportFilterCard because that shared component
 * doesn't model a month-range axis.
 */
import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FilterListIcon from '@mui/icons-material/FilterList'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enAU from 'date-fns/locale/en-AU'
import SearchField from '../../../../../components/shared/filters/fields/SearchField'
import FPStatusField from '../../../../../components/shared/filters/fields/FPStatusField'
import MarketField from '../../../../../components/shared/filters/fields/MarketField'
import RegionField from '../../../../../components/shared/filters/fields/RegionField'
import type { RegionOption } from '../../../../../components/shared/filters/types'
import { apiClient } from '../../../../../services/api'
import type { CogsTrackerFilters as CogsTrackerFiltersType } from './types'

interface CogsTrackerFiltersProps {
  filters: CogsTrackerFiltersType
  onChange: (key: keyof CogsTrackerFiltersType, value: string | undefined) => void
  onClear: () => void
  onRun: () => void
  onExport: () => void
  isLoading: boolean
  canExport: boolean
}

const MIN_MONTH = new Date(2024, 0)

const monthKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

const parseMonthKey = (value: string | undefined): Date | null => {
  if (!value) return null
  const [y, m] = value.split('-').map(Number)
  if (!y || !m) return null
  return new Date(y, m - 1, 1)
}

const CogsTrackerFilters: React.FC<CogsTrackerFiltersProps> = ({
  filters,
  onChange,
  onClear,
  onRun,
  onExport,
  isLoading,
  canExport,
}) => {
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([])
  const [loadingRegions, setLoadingRegions] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchRegions = async () => {
      if (!filters.market) {
        setRegionOptions([])
        return
      }
      setLoadingRegions(true)
      try {
        const response = await apiClient.get(
          `/admin/regions?market=${filters.market}&per_page=100`,
        )
        if (!cancelled) {
          setRegionOptions(response.data.regions || [])
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch regions:', error)
          setRegionOptions([])
        }
      } finally {
        if (!cancelled) setLoadingRegions(false)
      }
    }
    fetchRegions()
    return () => {
      cancelled = true
    }
  }, [filters.market])

  const today = new Date()
  const maxMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const startMonthDate = parseMonthKey(filters.start_month)
  const endMonthDate = parseMonthKey(filters.end_month)

  const handleMarketChange = (value: string) => {
    onChange('market', value || undefined)
    if (filters.region) onChange('region', undefined)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6">Filters</Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={onClear}
              disabled={isLoading}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}
            >
              Clear filters
            </Button>
          </Box>
          <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={4}>
          <SearchField
            label="Search"
            placeholder="FP code or name"
            value={filters.search || ''}
            onChange={(v) => onChange('search', v || undefined)}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FPStatusField
            value={filters.fp_status || ''}
            onChange={(v) => onChange('fp_status', v || undefined)}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <MarketField
            value={filters.market || ''}
            onChange={handleMarketChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RegionField
            value={filters.region || ''}
            onChange={(v) => onChange('region', v || undefined)}
            regionOptions={regionOptions}
            disabled={!filters.market}
            loading={loadingRegions}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="From"
            views={['year', 'month']}
            format="MMM yyyy"
            minDate={MIN_MONTH}
            maxDate={endMonthDate || maxMonth}
            value={startMonthDate}
            onChange={(d) =>
              onChange('start_month', d ? monthKey(d) : undefined)
            }
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="To"
            views={['year', 'month']}
            format="MMM yyyy"
            minDate={startMonthDate || MIN_MONTH}
            maxDate={maxMonth}
            value={endMonthDate}
            onChange={(d) =>
              onChange('end_month', d ? monthKey(d) : undefined)
            }
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={isLoading}
                onClick={onRun}
                sx={{
                  borderColor: '#E6E8EA',
                  color: '#1F2A2E',
                  px: 2,
                  '&:hover': {
                    borderColor: '#007f98',
                    backgroundColor: '#E6F4F5',
                  },
                }}
              >
                {isLoading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                disabled={!canExport || isLoading}
                onClick={onExport}
                sx={{
                  borderColor: '#E6E8EA',
                  color: '#1F2A2E',
                  px: 2,
                  '&:hover': {
                    borderColor: '#007f98',
                    backgroundColor: '#E6F4F5',
                  },
                }}
              >
                Export Excel
              </Button>
            </Box>
          </Grid>
          </Grid>
        </CardContent>
      </Card>
    </LocalizationProvider>
  )
}

export default CogsTrackerFilters
